import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchStartups, searchVcs } from "../lib/api.js";
import { getCategoryColor, VC_TYPE_COLORS, VC_TYPE_LABELS } from "../lib/constants.js";

function vibeScoreDisplay(score: number): number {
  return Math.round(((score + 2) / 4) * 100);
}

function vibeColorClass(score: number): string {
  if (score >= 1) return "text-emerald-400";
  if (score >= 0.3) return "text-lime-400";
  if (score >= -0.3) return "text-yellow-400";
  if (score >= -1) return "text-orange-400";
  return "text-red-400";
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

type SearchType = "all" | "startups" | "vcs";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const initialType = (searchParams.get("type") as SearchType) || "all";

  const [query, setQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState<SearchType>(initialType);
  const [results, setResults] = useState<any[]>([]);
  const [vcResults, setVcResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performSearch = useCallback(async (q: string, type: SearchType) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const promises: Promise<any>[] = [];

      if (type === "all" || type === "startups") {
        promises.push(searchStartups(q.trim()).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }

      if (type === "all" || type === "vcs") {
        promises.push(searchVcs(q.trim()).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }

      const [startupData, vcData] = await Promise.all(promises);
      setResults(startupData);
      setVcResults(vcData);
      setSearched(true);
    } catch (err: any) {
      setResults([]);
      setVcResults([]);
      setSearched(true);
      setError(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Search on mount if query param exists
  useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery, initialType);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    const params: Record<string, string> = { q: query.trim() };
    if (searchType !== "all") params.type = searchType;
    setSearchParams(params);
    performSearch(query.trim(), searchType);
  }

  function handleTypeChange(type: SearchType) {
    setSearchType(type);
    if (searched && query.trim()) {
      const params: Record<string, string> = { q: query.trim() };
      if (type !== "all") params.type = type;
      setSearchParams(params);
      performSearch(query.trim(), type);
    }
  }

  const totalResults = results.length + vcResults.length;

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold mb-2">
          <span className="gradient-text">Search</span>
        </h1>
        <p className="text-slate-500 text-sm">
          Find startups and VCs, check the vibes
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <svg
              className="w-4 h-4 text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, domain, or description..."
            className="input pl-10"
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="btn-primary disabled:opacity-50 !px-5"
        >
          {loading ? (
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            "Search"
          )}
        </button>
      </form>

      {/* Type toggle */}
      <div className="flex gap-2 mb-8">
        {([
          { value: "all" as const, label: "All" },
          { value: "startups" as const, label: "Startups" },
          { value: "vcs" as const, label: "VCs" },
        ]).map((t) => (
          <button
            key={t.value}
            onClick={() => handleTypeChange(t.value)}
            className={`tab ${
              searchType === t.value ? "tab-active" : "tab-inactive"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="card text-center py-8 mb-4">
          <div className="text-3xl mb-2">{"\u{1F635}"}</div>
          <p className="text-red-400 font-medium mb-1">Search failed</p>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {searched && !error && totalResults === 0 && (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">{"\u{1F50D}"}</div>
          <p className="text-slate-300 font-medium mb-1">
            No results found for "{query}"
          </p>
          <p className="text-slate-500 text-sm mb-4">
            Maybe it's not on StartupVibes yet?
          </p>
          <Link
            to="/submit"
            className="btn-primary inline-block text-sm"
          >
            Submit a startup {"\u{1F680}"}
          </Link>
        </div>
      )}

      {/* Startup Results */}
      {results.length > 0 && (
        <div className="mb-8">
          {(searchType === "all" && vcResults.length > 0) && (
            <h2 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wide">
              Startups ({results.length})
            </h2>
          )}
          <div className="space-y-3">
            {results.map((s) => (
              <Link
                key={s.id}
                to={`/startup/${s.slug}`}
                className="card-hover block group animate-fade-in"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-800/80 flex items-center justify-center shrink-0 border border-slate-700/30 overflow-hidden">
                    {s.logo_url ? (
                      <img
                        src={s.logo_url}
                        alt={s.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-bold text-slate-500">
                        {s.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-100 group-hover:text-white transition-colors">
                      {s.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sm text-slate-500">{s.domain}</span>
                      <span
                        className={`badge border text-[10px] ${getCategoryColor(s.category)}`}
                      >
                        {categoryLabel(s.category)}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-extrabold ${vibeColorClass(s.vibe_score)}`}
                  >
                    {vibeScoreDisplay(s.vibe_score)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* VC Results */}
      {vcResults.length > 0 && (
        <div>
          {(searchType === "all" && results.length > 0) && (
            <h2 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wide">
              VCs ({vcResults.length})
            </h2>
          )}
          <div className="space-y-3">
            {vcResults.map((v) => {
              const typeColor = VC_TYPE_COLORS[v.type] || "bg-slate-500/15 text-slate-300 border-slate-500/20";
              const typeLabel = VC_TYPE_LABELS[v.type] || v.type?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) || "VC";
              return (
                <Link
                  key={v.id}
                  to={`/vc/${v.slug}`}
                  className="card-hover block group animate-fade-in"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-slate-800/80 flex items-center justify-center shrink-0 border border-slate-700/30">
                      <span className="text-sm font-bold text-slate-500">
                        {v.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-100 group-hover:text-white transition-colors">
                        {v.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        {v.domain && (
                          <span className="text-sm text-slate-500">{v.domain}</span>
                        )}
                        <span className={`badge border text-[10px] ${typeColor}`}>
                          {typeLabel}
                        </span>
                        {v.portfolio_count > 0 && (
                          <span className="text-xs text-slate-500">
                            {v.portfolio_count} companies
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`text-2xl font-extrabold ${vibeColorClass(v.vibe_score)}`}
                    >
                      {vibeScoreDisplay(v.vibe_score)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
